import type { ReactNode } from 'react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { useMyMembership } from 'src/services/apollo/membership'
import { useCampaignAds } from 'src/services/orpc/campaignAds'
import { Preloader } from '../Preloader/Preloader'
import { AdLabel, AdMedia } from './AdUnitCommon'
import { trackCampaignClick } from './campaign'
import {
  checkAdUnitConditionWithMembership,
  useCampaignViewTracker,
} from './hooks'

export interface DailyQuestAdUnitProps {
  fallback?: () => ReactNode
}

export function DailyQuestAdUnit(props: DailyQuestAdUnitProps) {
  const { i18n } = useTranslation()
  const { data: { subscription } = {}, loading: isLoadingMembership } =
    useMyMembership()

  const { data, loading: isLoadingCampaign } = useCampaignAds({
    lang: i18n.language,
    category: 'dailyClaim',
  })
  // Find first campaign that is eligible to be displayed
  const campaignToShow = data?.find(it =>
    checkAdUnitConditionWithMembership(it, subscription),
  )

  const trackingParams = useMemo(
    () => ({
      category: 'daily_claim',
      scene: 'home',
      tracking_id: campaignToShow?.trackingId,
    }),
    [campaignToShow?.trackingId],
  )
  const containerRef = useCampaignViewTracker(trackingParams)

  if (isLoadingCampaign || isLoadingMembership)
    return (
      <div className="flex size-full aspect-square">
        <Preloader className="!my-auto" />
      </div>
    )

  return campaignToShow ? (
    <Link
      ref={containerRef}
      className="block size-full aspect-square rounded-lg overflow-hidden hover:no-underline"
      to={campaignToShow.targetUrl ?? '#'}
      target={'_blank'}
      onClick={() => {
        trackCampaignClick(trackingParams)
      }}
    >
      <AdMedia
        className="w-full aspect-square rounded-lg overflow-hidden"
        mediaType={campaignToShow.type}
        mediaSpec={campaignToShow.media}
      />
      {campaignToShow.isAd ? (
        <AdLabel className="absolute bottom-2 right-2" />
      ) : null}
    </Link>
  ) : (
    <>{props.fallback?.()}</>
  )
}
